import React, { useEffect, useMemo, useState } from 'react';
import { useChartTheme } from '../theme/chartTheme';

type FlowSide = 'buy' | 'sell' | 'mid';

export interface SteppedOrderFlowTrade {
  id: number | string;
  time: string;
  sessionDayYmd?: string;
  timestamp?: string | number;
  opra_trade_timestamp?: string;
  price: number;
  volume: number;
  side: string;
  premium?: number;
}

interface SteppedOrderFlowChartProps {
  trades: SteppedOrderFlowTrade[];
  title?: string;
}

interface BucketPoint {
  bucketMs: number;
  bucketLabel: string;
  stepPrice: number;
  buyVolume: number;
  sellVolume: number;
  buyPremium: number;
  sellPremium: number;
  netDelta: number;
  netPremium: number;
  imbalancePct: number;
  imbalanceSide: 'Buy Heavy' | 'Sell Heavy' | 'Balanced';
  ladderRows: Array<{
    price: number;
    buyVol: number;
    sellVol: number;
    delta: number;
    buyPrem: number;
    sellPrem: number;
  }>;
}

interface VolumeTag {
  key: string;
  bucketMs: number;
  bucketLabel: string;
  price: number;
  side: Exclude<FlowSide, 'mid'>;
  volume: number;
  premium: number;
}

interface NormalizedTrade {
  id: number | string;
  side: FlowSide;
  price: number;
  volume: number;
  premium: number;
  ts: number;
}

interface VolumeProfileRow {
  price: number;
  buyVol: number;
  sellVol: number;
  totalVol: number;
}

const POC = '#facc15';
const DEFAULT_VISIBLE_BUCKETS = 10;
const MIN_VISIBLE_BUCKETS = 3;
const DEFAULT_RIGHT_SHIFT_BUCKETS = 2;

function toMs(trade: SteppedOrderFlowTrade): number {
  const raw = trade.timestamp ?? trade.opra_trade_timestamp;
  if (raw != null && raw !== '') {
    if (typeof raw === 'number') {
      let ms = raw;
      if (ms > 1e15) ms = Math.floor(ms / 1e6);
      else if (ms < 1e11) ms *= 1000;
      return Number.isFinite(ms) ? ms : 0;
    }
    const d = new Date(String(raw).trim());
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }

  const t = (trade.time || '').trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(t)) {
    const ymd = (trade.sessionDayYmd || new Date().toISOString().slice(0, 10)).slice(0, 10);
    const d = new Date(`${ymd}T${t}`);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  const d2 = new Date(t);
  return Number.isNaN(d2.getTime()) ? 0 : d2.getTime();
}

function hhmm(ms: number): string {
  const d = new Date(ms);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

const SteppedOrderFlowChart: React.FC<SteppedOrderFlowChartProps> = ({
  trades,
  title = 'Historical TSX Options Flow - Buy/Sell Volume on Price Line',
}) => {
  const ct = useChartTheme();
  // Chrome colours follow the palette; SVG paints from the computed values, HTML from the tokens.
  const BG = 'var(--elev)';
  const PLOT_BG = 'var(--base)';
  const GRID = ct.grid;
  const TEXT = 'var(--txt)';
  const SUB = ct.text;
  const STEP_BLUE = ct.accent;
  const BUY = ct.up;
  const SELL = ct.down;
  const [bucketMinutes, setBucketMinutes] = useState<1 | 5 | 15>(1);
  const [chartMode, setChartMode] = useState<'step' | 'line' | 'ticks' | 'bars'>('step');
  const [netMetricMode, setNetMetricMode] = useState<'delta' | 'premium'>('delta');
  const [showTradeTags, setShowTradeTags] = useState(true);
  const [tradePaneMode, setTradePaneMode] = useState<'bars' | 'squares'>('bars');
  const [rightShiftBuckets, setRightShiftBuckets] = useState(DEFAULT_RIGHT_SHIFT_BUCKETS);
  const [designView, setDesignView] = useState<'quant' | 'sierra' | 'hybrid'>('quant');
  const model = useMemo(() => {
    const clean = (trades || [])
      .map((t) => ({
        ...t,
        side: String(t.side || 'mid').toLowerCase() as FlowSide,
        price: Number(t.price) || 0,
        volume: Number(t.volume) || 0,
        premium: Number(t.premium) || (Number(t.price) || 0) * (Number(t.volume) || 0) * 100,
        ts: toMs(t),
      }))
      .filter((t) => Number.isFinite(t.ts) && t.ts > 0 && t.price > 0 && t.volume > 0)
      .sort((a, b) => a.ts - b.ts);

    if (clean.length === 0) {
      return {
        buckets: [] as BucketPoint[],
        tags: [] as VolumeTag[],
        cleanTrades: [] as NormalizedTrade[],
        profileRows: [] as VolumeProfileRow[],
        pocPrice: 0,
        minPrice: 0,
        maxPrice: 1,
      };
    }

    const bucketSizeMs = bucketMinutes * 60000;
    const minuteMap = new Map<number, typeof clean>();
    clean.forEach((t) => {
      const bucketMs = Math.floor(t.ts / bucketSizeMs) * bucketSizeMs;
      const arr = minuteMap.get(bucketMs) || [];
      arr.push(t);
      minuteMap.set(bucketMs, arr);
    });

    const tags: VolumeTag[] = [];
    const buckets: BucketPoint[] = [];
    const totalByPrice = new Map<number, number>();
    const profileByPrice = new Map<number, { buyVol: number; sellVol: number }>();

    const sortedMinuteKeys = Array.from(minuteMap.keys()).sort((a, b) => a - b);
    sortedMinuteKeys.forEach((bucketMs) => {
      const rows = minuteMap.get(bucketMs) || [];
      const byPriceSide = new Map<string, { price: number; side: 'buy' | 'sell'; volume: number; premium: number }>();
      const byPriceTotal = new Map<number, number>();
      const ladderByPrice = new Map<number, { buyVol: number; sellVol: number; buyPrem: number; sellPrem: number }>();
      let buyVolume = 0;
      let sellVolume = 0;
      let buyPremium = 0;
      let sellPremium = 0;

      rows.forEach((r) => {
        if (r.side === 'buy') {
          buyVolume += r.volume;
          buyPremium += r.premium;
          const p = profileByPrice.get(r.price) || { buyVol: 0, sellVol: 0 };
          p.buyVol += r.volume;
          profileByPrice.set(r.price, p);
          const l = ladderByPrice.get(r.price) || { buyVol: 0, sellVol: 0, buyPrem: 0, sellPrem: 0 };
          l.buyVol += r.volume;
          l.buyPrem += r.premium;
          ladderByPrice.set(r.price, l);
        } else if (r.side === 'sell') {
          sellVolume += r.volume;
          sellPremium += r.premium;
          const p = profileByPrice.get(r.price) || { buyVol: 0, sellVol: 0 };
          p.sellVol += r.volume;
          profileByPrice.set(r.price, p);
          const l = ladderByPrice.get(r.price) || { buyVol: 0, sellVol: 0, buyPrem: 0, sellPrem: 0 };
          l.sellVol += r.volume;
          l.sellPrem += r.premium;
          ladderByPrice.set(r.price, l);
        }
        const psKey = `${r.price}__${r.side}`;
        if (r.side === 'buy' || r.side === 'sell') {
          const ps = byPriceSide.get(psKey) || { price: r.price, side: r.side, volume: 0, premium: 0 };
          ps.volume += r.volume;
          ps.premium += r.premium;
          byPriceSide.set(psKey, ps);
        }
        byPriceTotal.set(r.price, (byPriceTotal.get(r.price) || 0) + r.volume);
        totalByPrice.set(r.price, (totalByPrice.get(r.price) || 0) + r.volume);
      });

      const topPrice = Array.from(byPriceTotal.entries()).sort((a, b) => b[1] - a[1] || b[0] - a[0])[0]?.[0] ?? rows[0].price;
      const netDelta = buyVolume - sellVolume;
      const netPremium = buyPremium - sellPremium;
      const gross = buyVolume + sellVolume;
      const imbalancePct = gross > 0 ? (Math.abs(netDelta) / gross) * 100 : 0;
      const imbalanceSide: BucketPoint['imbalanceSide'] =
        netDelta > 0 ? 'Buy Heavy' : netDelta < 0 ? 'Sell Heavy' : 'Balanced';

      buckets.push({
        bucketMs,
        bucketLabel: hhmm(bucketMs),
        stepPrice: topPrice,
        buyVolume,
        sellVolume,
        buyPremium,
        sellPremium,
        netDelta,
        netPremium,
        imbalancePct,
        imbalanceSide,
        ladderRows: Array.from(ladderByPrice.entries())
          .map(([price, x]) => ({
            price,
            buyVol: x.buyVol,
            sellVol: x.sellVol,
            delta: x.buyVol - x.sellVol,
            buyPrem: x.buyPrem,
            sellPrem: x.sellPrem,
          }))
          .sort((a, b) => b.price - a.price),
      });

      Array.from(byPriceSide.values())
        .sort((a, b) => b.volume - a.volume)
        .slice(0, 3)
        .forEach((x, i) => {
          tags.push({
            key: `${bucketMs}-${x.price}-${x.side}-${i}`,
            bucketMs,
            bucketLabel: hhmm(bucketMs),
            price: x.price,
            side: x.side,
            volume: x.volume,
            premium: x.premium,
          });
        });
    });

    const prices = buckets.map((b) => b.stepPrice);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const pocPrice = Array.from(totalByPrice.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? minPrice;
    const profileRows: VolumeProfileRow[] = Array.from(profileByPrice.entries())
      .map(([price, v]) => ({
        price,
        buyVol: v.buyVol,
        sellVol: v.sellVol,
        totalVol: v.buyVol + v.sellVol,
      }))
      .sort((a, b) => b.price - a.price);

    return {
      buckets,
      tags,
      cleanTrades: clean as NormalizedTrade[],
      profileRows,
      pocPrice,
      minPrice,
      maxPrice: maxPrice === minPrice ? minPrice + 0.01 : maxPrice,
    };
  }, [trades, bucketMinutes]);
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_BUCKETS);
  const [endIndex, setEndIndex] = useState(-1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const totalBuckets = model.buckets.length;

  useEffect(() => {
    setVisibleCount(Math.min(DEFAULT_VISIBLE_BUCKETS, totalBuckets));
    setEndIndex(totalBuckets - 1);
  }, [totalBuckets]);

  useEffect(() => {
    if (totalBuckets <= 0) return;
    setEndIndex((prev) => {
      const maxEnd = totalBuckets - 1;
      const minEnd = Math.min(maxEnd, Math.max(0, visibleCount - 1));
      const base = prev < 0 ? maxEnd : prev;
      return Math.min(maxEnd, Math.max(minEnd, base));
    });
  }, [totalBuckets, visibleCount]);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen]);

  if (model.buckets.length === 0) {
    return (
      <div className="flex items-center justify-center h-full rounded-lg" style={{ background: BG, color: SUB }}>
        No trade data available
      </div>
    );
  }

  const width = 1000;
  const height = 460;
  const pad = { left: 78, right: 28, top: 38, bottom: 122 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;
  const maxEnd = totalBuckets - 1;
  const minEnd = Math.min(maxEnd, Math.max(0, visibleCount - 1));
  const safeEnd = Math.min(maxEnd, Math.max(minEnd, endIndex < 0 ? maxEnd : endIndex));
  const safeStart = Math.max(0, safeEnd - visibleCount + 1);
  const visibleBuckets = model.buckets.slice(safeStart, safeEnd + 1);
  const visibleBucketMs = new Set(visibleBuckets.map((b) => b.bucketMs));
  const visibleTags = model.tags.filter((t) => visibleBucketMs.has(t.bucketMs));
  const visiblePrices = visibleBuckets.map((b) => b.stepPrice);
  const visibleMinPrice = Math.min(...visiblePrices);
  const visibleMaxRaw = Math.max(...visiblePrices);
  const visibleMaxPrice = visibleMaxRaw === visibleMinPrice ? visibleMinPrice + 0.01 : visibleMaxRaw;
  const n = visibleBuckets.length;
  const xDenominator = Math.max(1, n - 1 + rightShiftBuckets);
  const xForIndex = (i: number) => pad.left + (chartW * (n <= 1 ? 0 : i / xDenominator));
  const yForPrice = (p: number) => {
    const frac = (p - visibleMinPrice) / (visibleMaxPrice - visibleMinPrice || 1);
    return pad.top + chartH - frac * chartH;
  };

  const points: string[] = [];
  visibleBuckets.forEach((b, i) => {
    const x = xForIndex(i);
    const y = yForPrice(b.stepPrice);
    if (i === 0) {
      points.push(`M ${x} ${y}`);
    } else {
      points.push(`L ${x} ${yForPrice(visibleBuckets[i - 1].stepPrice)} L ${x} ${y}`);
    }
  });

  const shownXTicks = visibleBuckets.filter((_, i) => i % Math.max(1, Math.floor(visibleBuckets.length / 10)) === 0);
  const priceTicksRaw = Array.from(new Set(visibleBuckets.map((b) => Number(b.stepPrice.toFixed(2))))).sort((a, b) => a - b);
  const priceTicks = priceTicksRaw.length > 12 ? priceTicksRaw.filter((_, i) => i % Math.ceil(priceTicksRaw.length / 12) === 0) : priceTicksRaw;
  const canZoomIn = visibleCount > MIN_VISIBLE_BUCKETS;
  const canZoomOut = visibleCount < totalBuckets;
  const showPan = totalBuckets > visibleCount;
  const showPoc = model.pocPrice >= visibleMinPrice && model.pocPrice <= visibleMaxPrice;
  const formattedWindow = `${visibleBuckets[0]?.bucketLabel ?? '--:--'} - ${visibleBuckets[visibleBuckets.length - 1]?.bucketLabel ?? '--:--'}`;
  const windowStartMs = visibleBuckets[0]?.bucketMs ?? 0;
  const windowEndMs = (visibleBuckets[visibleBuckets.length - 1]?.bucketMs ?? 0) + bucketMinutes * 60000;
  const visibleTrades = model.cleanTrades
    .filter((t) => t.ts >= windowStartMs && t.ts < windowEndMs)
    .slice(-80)
    .reverse();
  const activeBucket = visibleBuckets[visibleBuckets.length - 1];
  const ladderRows = (activeBucket?.ladderRows || []).slice(0, 22);
  const ladderMaxVol = Math.max(1, ...ladderRows.map((r) => Math.max(r.buyVol, r.sellVol)));
  const profileRowsWindow = model.profileRows
    .filter((r) => r.price >= visibleMinPrice - 0.01 && r.price <= visibleMaxPrice + 0.01)
    .slice(0, 28);
  const profileMax = Math.max(1, ...profileRowsWindow.map((r) => r.totalVol));

  const linePath = (() => {
    const pointsLine: string[] = [];
    visibleBuckets.forEach((b, i) => {
      const x = xForIndex(i);
      const y = yForPrice(b.stepPrice);
      pointsLine.push(i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`);
    });
    return pointsLine.join(' ');
  })();

  const shellClass = isFullscreen
    ? 'fixed inset-0 z-[1200] p-4 md:p-6'
    : 'w-full h-full rounded-lg p-3';
  const shellStyle: React.CSSProperties = isFullscreen
    ? { background: 'color-mix(in srgb, var(--void) 95%, transparent)', color: TEXT }
    : { background: BG, color: TEXT, border: '1px solid var(--hair)', borderRadius: 10 };

  const netValues = visibleBuckets.map((b) => (netMetricMode === 'premium' ? b.netPremium : b.netDelta));
  const netMaxAbs = Math.max(1, ...netValues.map((v) => Math.abs(v)));

  return (
    <div className={shellClass} style={shellStyle}>
      <div className={isFullscreen ? 'w-full h-full rounded-lg p-3 md:p-4 shadow-2xl' : ''} style={isFullscreen ? { background: BG } : undefined}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold" style={{ color: TEXT, fontSize: 14 }}>{title}</h4>
        <div className="flex items-center gap-3 text-xs flex-wrap justify-end" style={{ color: SUB }}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <label>TF</label>
            <select
              value={bucketMinutes}
              onChange={(e) => setBucketMinutes(Number(e.target.value) as 1 | 5 | 15)}
              className="rounded border px-1.5 py-1"
              style={{ borderColor: 'var(--border)', background: 'var(--elev)', color: TEXT }}
            >
              <option value={1}>1m</option>
              <option value={5}>5m</option>
              <option value={15}>15m</option>
            </select>
            <label>View</label>
            <select
              value={chartMode}
              onChange={(e) => setChartMode(e.target.value as 'step' | 'line' | 'ticks' | 'bars')}
              className="rounded border px-1.5 py-1"
              style={{ borderColor: 'var(--border)', background: 'var(--elev)', color: TEXT }}
            >
              <option value="step">Step</option>
              <option value="line">Line</option>
              <option value="ticks">Ticks</option>
              <option value="bars">Bars</option>
            </select>
            <label>Design</label>
            <select
              value={designView}
              onChange={(e) => setDesignView(e.target.value as 'quant' | 'sierra' | 'hybrid')}
              className="rounded border px-1.5 py-1"
              style={{ borderColor: 'var(--border)', background: 'var(--elev)', color: TEXT }}
            >
              <option value="quant">Quant Tape</option>
              <option value="sierra">Sierra Footprint</option>
              <option value="hybrid">Hybrid CVD</option>
            </select>
            <label>Net</label>
            <select
              value={netMetricMode}
              onChange={(e) => setNetMetricMode(e.target.value as 'delta' | 'premium')}
              className="rounded border px-1.5 py-1"
              style={{ borderColor: 'var(--border)', background: 'var(--elev)', color: TEXT }}
            >
              <option value="delta">Delta</option>
              <option value="premium">Premium</option>
            </select>
            {designView === 'quant' && (
              <>
                <label>Trade pane</label>
                <select
                  value={tradePaneMode}
                  onChange={(e) => setTradePaneMode(e.target.value as 'bars' | 'squares')}
                  className="rounded border px-1.5 py-1"
                  style={{ borderColor: 'var(--border)', background: 'var(--elev)', color: TEXT }}
                >
                  <option value="bars">Bars</option>
                  <option value="squares">Squares</option>
                </select>
              </>
            )}
            <button
              type="button"
              onClick={() => setShowTradeTags((v) => !v)}
              className="mmec-btn"
            >
              {showTradeTags ? 'Hide trades' : 'Show trades'}
            </button>
            <button
              type="button"
              onClick={() => setIsFullscreen((v) => !v)}
              className="mmec-btn"
            >
              {isFullscreen ? 'Exit full screen' : 'Full screen'}
            </button>
            <button
              type="button"
              onClick={() => setVisibleCount((v) => Math.max(MIN_VISIBLE_BUCKETS, Math.floor(v * 0.8)))}
              disabled={!canZoomIn}
              className="mmec-btn disabled:opacity-40"
            >
              Zoom +
            </button>
            <button
              type="button"
              onClick={() => setVisibleCount((v) => Math.min(totalBuckets, Math.ceil(v * 1.25)))}
              disabled={!canZoomOut}
              className="mmec-btn disabled:opacity-40"
            >
              Zoom -
            </button>
            <button
              type="button"
              onClick={() => {
                setVisibleCount(Math.min(DEFAULT_VISIBLE_BUCKETS, totalBuckets));
                setEndIndex(totalBuckets - 1);
              }}
              className="mmec-btn"
            >
              Reset
            </button>
          </div>
          <span>
            Showing {visibleBuckets.length} of {totalBuckets} buckets
          </span>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: BUY }} /> Buy Volume</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: SELL }} /> Sell Volume</div>
        </div>
      </div>
      {showPan && (
        <div className="mb-2 space-y-1">
          <input
            type="range"
            min={minEnd}
            max={maxEnd}
            value={safeEnd}
            onChange={(e) => setEndIndex(Number(e.target.value))}
            className="w-full"
          />
          <div className="flex items-center gap-2">
            <span className="text-[11px]" style={{ color: SUB }}>
              View window: {formattedWindow}
            </span>
            <span className="text-[11px]" style={{ color: SUB }}>
              Latest shift
            </span>
            <input
              type="range"
              min={0}
              max={8}
              value={rightShiftBuckets}
              onChange={(e) => setRightShiftBuckets(Number(e.target.value))}
              className="w-28"
            />
            <span className="text-[11px]" style={{ color: SUB }}>{rightShiftBuckets}</span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {designView !== 'sierra' && (
          <div>
            <div className="w-full overflow-x-auto">
              <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ background: PLOT_BG, borderRadius: 10, border: '1px solid var(--hair)' }}>
                {priceTicks.map((p) => {
                  const y = yForPrice(p);
                  return <line key={`gy-${p}`} x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke={GRID} strokeWidth={1} />;
                })}
                {shownXTicks.map((t, i) => {
                  const ix = visibleBuckets.findIndex((b) => b.bucketMs === t.bucketMs);
                  const x = xForIndex(ix);
                  return <line key={`gx-${i}`} x1={x} x2={x} y1={pad.top} y2={pad.top + chartH} stroke={GRID} strokeWidth={1} />;
                })}

                {showPoc && (
                  <>
                    <line
                      x1={pad.left}
                      x2={width - pad.right}
                      y1={yForPrice(model.pocPrice)}
                      y2={yForPrice(model.pocPrice)}
                      stroke={POC}
                      strokeWidth={1.5}
                    />
                    <text x={width - pad.right - 6} y={yForPrice(model.pocPrice) - 6} textAnchor="end" fill={POC} fontSize={11} fontWeight={700}>
                      POC {model.pocPrice.toFixed(2)}
                    </text>
                  </>
                )}

                {chartMode === 'step' && <path d={points.join(' ')} fill="none" stroke={STEP_BLUE} strokeWidth={2.5} />}
                {chartMode === 'line' && <path d={linePath} fill="none" stroke={STEP_BLUE} strokeWidth={2.5} />}

                {chartMode === 'bars' &&
                  visibleBuckets.map((b, i) => {
                    const x = xForIndex(i);
                    const y = yForPrice(b.stepPrice);
                    return <line key={`bar-${b.bucketMs}`} x1={x} x2={x} y1={pad.top + chartH} y2={y} stroke={STEP_BLUE} strokeWidth={2} />;
                  })}

                {visibleBuckets.map((b, i) => {
                  const x = xForIndex(i);
                  const y = yForPrice(b.stepPrice);
                  return (
                    <g key={`pt-${b.bucketMs}`}>
                      <circle cx={x} cy={y} r={chartMode === 'ticks' ? 4 : 3.2} fill={STEP_BLUE} />
                      <title>
                        {`${b.bucketLabel} | Price ${b.stepPrice.toFixed(2)} | Buy ${Math.round(b.buyVolume)} | Sell ${Math.round(b.sellVolume)} | Net ${b.netDelta >= 0 ? '+' : ''}${Math.round(b.netDelta)}`}
                      </title>
                    </g>
                  );
                })}

                {designView !== 'quant' && showTradeTags && visibleTags.map((tag, i) => {
                  const idx = visibleBuckets.findIndex((b) => b.bucketMs === tag.bucketMs);
                  const x = xForIndex(idx);
                  const y = yForPrice(tag.price);
                  const rightSide = i % 2 === 0;
                  const dx = rightSide ? 64 : -64;
                  const labelX = x + dx;
                  const labelY = y + (tag.side === 'buy' ? -10 : 10);
                  const color = tag.side === 'buy' ? BUY : SELL;
                  const txt = `${tag.side === 'buy' ? 'Buy' : 'Sell'} ${Math.round(tag.volume)}`;
                  return (
                    <g key={tag.key}>
                      <line x1={x} y1={y} x2={labelX} y2={labelY} stroke={color} strokeWidth={1} strokeDasharray="2 2" />
                      <rect
                        x={labelX - (rightSide ? 0 : 56)}
                        y={labelY - 10}
                        width={56}
                        height={18}
                        rx={4}
                        ry={4}
                        fill={color}
                      />
                      <text
                        x={labelX - 28}
                        y={labelY + 3}
                        textAnchor="middle"
                        fill="#ffffff"
                        fontSize={10}
                        fontWeight={700}
                      >
                        {txt}
                      </text>
                      <title>{`${tag.bucketLabel} @ ${tag.price.toFixed(2)} | ${txt} | Premium $${tag.premium.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}</title>
                    </g>
                  );
                })}

                <text x={16} y={pad.top - 10} fill={SUB} fontSize={11}>Option Price</text>
                {priceTicks.map((p) => (
                  <text key={`yl-${p}`} x={pad.left - 8} y={yForPrice(p) + 4} textAnchor="end" fill={SUB} fontSize={11}>
                    {p.toFixed(2)}
                  </text>
                ))}

                <text x={width / 2} y={height - 8} textAnchor="middle" fill={SUB} fontSize={11}>Time</text>
                {shownXTicks.map((t, i) => {
                  const ix = visibleBuckets.findIndex((b) => b.bucketMs === t.bucketMs);
                  return (
                    <text key={`xl-${i}`} x={xForIndex(ix)} y={pad.top + chartH + 20} textAnchor="middle" fill={SUB} fontSize={11}>
                      {t.bucketLabel}
                    </text>
                  );
                })}

                {designView !== 'quant' && shownXTicks.map((t, i) => {
                  const ix = visibleBuckets.findIndex((b) => b.bucketMs === t.bucketMs);
                  const x = xForIndex(ix);
                  const metricValue = netMetricMode === 'premium' ? t.netPremium : t.netDelta;
                  const c = metricValue > 0 ? BUY : metricValue < 0 ? SELL : SUB;
                  const txt1 = netMetricMode === 'premium'
                    ? `Net Prem: ${metricValue >= 0 ? '+' : ''}$${Math.round(metricValue).toLocaleString()}`
                    : `Net Delta: ${metricValue >= 0 ? '+' : ''}${Math.round(metricValue)}`;
                  const txt2 = `${t.imbalanceSide} ${Math.round(t.imbalancePct)}%`;
                  return (
                    <g key={`nd-${i}`}>
                      <rect x={x - 42} y={pad.top + chartH + 32} width={84} height={34} rx={5} ry={5} fill={ct.background} stroke={c} />
                      <text x={x} y={pad.top + chartH + 45} textAnchor="middle" fill={c} fontSize={9} fontWeight={700}>{txt1}</text>
                      <text x={x} y={pad.top + chartH + 57} textAnchor="middle" fill={c} fontSize={9}>{txt2}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
            {designView === 'quant' && (
              <div className="mt-2 rounded-lg border p-2" style={{ borderColor: GRID, background: PLOT_BG }}>
                <div className="text-xs font-semibold mb-2" style={{ color: TEXT }}>
                  Trade Flow Pane ({tradePaneMode === 'bars' ? 'volume bars' : 'delta squares'})
                </div>
                {tradePaneMode === 'bars' ? (
                  <div className="space-y-1">
                    {visibleBuckets.map((b) => {
                      const gross = Math.max(1, b.buyVolume + b.sellVolume);
                      const buyW = `${(b.buyVolume / gross) * 100}%`;
                      const sellW = `${(b.sellVolume / gross) * 100}%`;
                      return (
                        <div key={`tb-${b.bucketMs}`} className="grid grid-cols-12 items-center gap-2 text-[11px]">
                          <div className="col-span-1" style={{ color: SUB }}>{b.bucketLabel}</div>
                          <div className="col-span-5 h-3 rounded overflow-hidden" style={{ background: 'var(--down-dim)' }}>
                            <div style={{ width: sellW, height: '100%', background: 'color-mix(in srgb, var(--down) 55%, transparent)' }} />
                          </div>
                          <div className="col-span-5 h-3 rounded overflow-hidden" style={{ background: 'var(--up-dim)' }}>
                            <div style={{ width: buyW, height: '100%', background: 'color-mix(in srgb, var(--up) 55%, transparent)' }} />
                          </div>
                          <div className="col-span-1 text-right" style={{ color: b.netDelta >= 0 ? BUY : SELL }}>
                            {`${b.netDelta >= 0 ? '+' : ''}${Math.round(b.netDelta)}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-10 gap-1">
                    {visibleBuckets.map((b) => {
                      const c = b.netDelta >= 0 ? BUY : SELL;
                      const intensity = Math.min(1, Math.abs(b.netDelta) / Math.max(1, netMaxAbs));
                      return (
                        <div
                          key={`sq-${b.bucketMs}`}
                          className="rounded p-1 text-[10px] text-center"
                          style={{ background: c, opacity: 0.25 + intensity * 0.75, color: '#fff' }}
                          title={`${b.bucketLabel} | Buy ${Math.round(b.buyVolume)} | Sell ${Math.round(b.sellVolume)} | Net ${Math.round(b.netDelta)}`}
                        >
                          <div>{b.bucketLabel}</div>
                          <div>{Math.round(b.netDelta)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {designView === 'quant' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <div className="rounded-lg border p-2" style={{ borderColor: GRID, background: PLOT_BG }}>
              <div className="text-xs font-semibold mb-2" style={{ color: TEXT }}>
                DOM Ladder (active bucket {activeBucket?.bucketLabel ?? '--:--'})
              </div>
              <div className="space-y-1 max-h-[250px] overflow-auto">
                {ladderRows.map((r) => {
                  const bidW = `${(r.sellVol / ladderMaxVol) * 100}%`;
                  const askW = `${(r.buyVol / ladderMaxVol) * 100}%`;
                  const d = r.delta;
                  return (
                    <div key={`lad-${r.price}`} className="grid grid-cols-12 gap-1 text-[11px] items-center">
                      <div className="col-span-4 relative h-5 rounded overflow-hidden" style={{ background: 'var(--down-dim)' }}>
                        <div style={{ width: bidW, height: '100%', background: 'color-mix(in srgb, var(--down) 45%, transparent)' }} />
                        <div className="absolute inset-0 px-1 flex items-center justify-end" style={{ color: 'var(--down)' }}>{Math.round(r.sellVol)}</div>
                      </div>
                      <div className="col-span-3 text-center font-semibold" style={{ color: TEXT }}>{r.price.toFixed(2)}</div>
                      <div className="col-span-4 relative h-5 rounded overflow-hidden" style={{ background: 'var(--up-dim)' }}>
                        <div style={{ width: askW, height: '100%', background: 'color-mix(in srgb, var(--up) 45%, transparent)' }} />
                        <div className="absolute inset-0 px-1 flex items-center justify-start" style={{ color: 'var(--up)' }}>{Math.round(r.buyVol)}</div>
                      </div>
                      <div className="col-span-1 text-right" style={{ color: d >= 0 ? BUY : SELL }}>{`${d >= 0 ? '+' : ''}${Math.round(d)}`}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="rounded-lg border p-2" style={{ borderColor: GRID, background: PLOT_BG }}>
              <div className="text-xs font-semibold mb-2" style={{ color: TEXT }}>Time & Sales (latest)</div>
              <div className="max-h-[190px] overflow-auto space-y-1">
                {visibleTrades.map((t) => {
                  const sideColor = t.side === 'buy' ? BUY : t.side === 'sell' ? SELL : SUB;
                  return (
                    <div key={`tape-${t.id}-${t.ts}`} className="rounded px-2 py-1 text-[11px] grid grid-cols-5 gap-2" style={{ background: 'var(--elev2)' }}>
                      <span style={{ color: SUB }}>{hhmm(t.ts)}</span>
                      <span style={{ color: TEXT }}>${t.price.toFixed(2)}</span>
                      <span style={{ color: sideColor, fontWeight: 700 }}>{t.side.toUpperCase()}</span>
                      <span style={{ color: TEXT }} className="text-right">{Math.round(t.volume)}</span>
                      <span style={{ color: TEXT }} className="text-right">${Math.round(t.premium).toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {designView === 'sierra' && (
          <div className="rounded-lg border p-2 overflow-auto" style={{ borderColor: GRID, background: PLOT_BG }}>
            <div className="text-xs font-semibold mb-2" style={{ color: TEXT }}>Footprint Clusters</div>
            <div className="grid gap-2" style={{ gridTemplateColumns: `120px repeat(${visibleBuckets.length}, minmax(64px, 1fr))` }}>
              <div className="text-[11px]" style={{ color: SUB }}>Price</div>
              {visibleBuckets.map((b) => (
                <div key={`fh-${b.bucketMs}`} className="text-center text-[11px]" style={{ color: SUB }}>{b.bucketLabel}</div>
              ))}
              {Array.from(
                new Set(
                  visibleBuckets.flatMap((b) => b.ladderRows.map((r) => Number(r.price.toFixed(2))))
                )
              )
                .sort((a, b) => b - a)
                .slice(0, 22)
                .map((price) => (
                  <React.Fragment key={`row-${price}`}>
                    <div className="text-[11px] py-1" style={{ color: TEXT }}>{price.toFixed(2)}</div>
                    {visibleBuckets.map((b) => {
                      const row = b.ladderRows.find((r) => Number(r.price.toFixed(2)) === price);
                      if (!row) return <div key={`empty-${b.bucketMs}-${price}`} className="h-7 rounded" style={{ background: 'var(--hair)' }} />;
                      const d = row.delta;
                      const bg = d >= 0 ? 'color-mix(in srgb, var(--up) 28%, transparent)' : 'color-mix(in srgb, var(--down) 28%, transparent)';
                      return (
                        <div key={`cell-${b.bucketMs}-${price}`} className="h-7 rounded px-1 text-[10px] flex items-center justify-between" style={{ background: bg }}>
                          <span style={{ color: 'var(--down)' }}>{Math.round(row.sellVol)}</span>
                          <span style={{ color: 'var(--up)' }}>{Math.round(row.buyVol)}</span>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
            </div>
          </div>
        )}

        {designView === 'hybrid' && (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
            <div className="xl:col-span-7 rounded-lg border p-2" style={{ borderColor: GRID, background: PLOT_BG }}>
              <div className="text-xs font-semibold mb-2" style={{ color: TEXT }}>
                {netMetricMode === 'premium' ? 'Net Premium' : 'Net Delta'} Histogram
              </div>
              <div className="space-y-1">
                {visibleBuckets.map((b, i) => {
                  const v = netValues[i] ?? 0;
                  const wPct = Math.max(2, (Math.abs(v) / netMaxAbs) * 100);
                  const barColor = v >= 0 ? BUY : SELL;
                  return (
                    <div key={`hy-${b.bucketMs}`} className="flex items-center gap-2 text-[11px]">
                      <div className="w-12" style={{ color: SUB }}>{b.bucketLabel}</div>
                      <div className="flex-1 h-3 rounded" style={{ background: 'var(--elev2)' }}>
                        <div className="h-3 rounded" style={{ width: `${wPct}%`, background: barColor }} />
                      </div>
                      <div className="w-28 text-right" style={{ color: barColor }}>
                        {netMetricMode === 'premium'
                          ? `${v >= 0 ? '+' : ''}$${Math.round(v).toLocaleString()}`
                          : `${v >= 0 ? '+' : ''}${Math.round(v)}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="xl:col-span-5 rounded-lg border p-2" style={{ borderColor: GRID, background: PLOT_BG }}>
              <div className="text-xs font-semibold mb-2" style={{ color: TEXT }}>Volume Profile (window)</div>
              <div className="space-y-1 max-h-[360px] overflow-auto">
                {profileRowsWindow.map((r) => {
                  const buyW = `${(r.buyVol / profileMax) * 100}%`;
                  const sellW = `${(r.sellVol / profileMax) * 100}%`;
                  return (
                    <div key={`vp-${r.price}`} className="grid grid-cols-12 gap-1 text-[11px] items-center">
                      <div className="col-span-3 text-right" style={{ color: TEXT }}>{r.price.toFixed(2)}</div>
                      <div className="col-span-4 h-3 rounded overflow-hidden" style={{ background: 'var(--down-dim)' }}>
                        <div style={{ width: sellW, height: '100%', background: 'color-mix(in srgb, var(--down) 50%, transparent)' }} />
                      </div>
                      <div className="col-span-4 h-3 rounded overflow-hidden" style={{ background: 'var(--up-dim)' }}>
                        <div style={{ width: buyW, height: '100%', background: 'color-mix(in srgb, var(--up) 50%, transparent)' }} />
                      </div>
                      <div className="col-span-1 text-right" style={{ color: SUB }}>{Math.round(r.totalVol)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default SteppedOrderFlowChart;
